import { useEffect, useRef } from 'react';

const DEFAULT_INTERVAL_MS = 10000;

// Shared polling refresh for every list/report page - calls `reload` on an
// interval so managers/dispatchers see new tickets/rows without a manual
// F5. Skipped while the tab is hidden (no point hitting the API for a page
// nobody is looking at) and while `paused` is true (pass this when the user
// has a form/modal open on the same page, so a background refresh can't
// wipe out what they're typing).
export function useAutoRefresh(reload, { intervalMs = DEFAULT_INTERVAL_MS, enabled = true, paused = false } = {}) {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    if (!enabled) return undefined;

    const tick = () => {
      if (document.visibilityState === 'visible' && !paused) {
        reloadRef.current();
      }
    };

    const intervalId = setInterval(tick, intervalMs);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, paused, intervalMs]);
}
